import React, { useState, useMemo } from "react";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote,
  Send, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw, Inbox, Search,
  CheckCircle2, Mail, MessageSquare, Smartphone, Eye, MousePointerClick, FileWarning,
} from "lucide-react";

/* Radha — Module 5: Invoice Generation + Multi-Channel Engagement (preview)
   Seeded to match 005_invoices.sql. GST: HSN mandatory, IRN before dispatch. */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, Menlo, monospace";
const sans = "'Inter', system-ui, sans-serif";
function formatINR(n) {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n), s = n < 0 ? "-" : "";
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${s}₹${(a / 1e5).toFixed(1)}L`;
  return `${s}₹${a.toLocaleString("en-IN")}`;
}

const STATUS_COLOR = {
  paid: C.success, delivered: C.primary, dispatched: C.primary, ready: C.muted,
  irn_pending: C.warning, overdue: C.danger, disputed: C.danger, draft: C.muted, cancelled: C.muted,
};

/* 8 invoices from 005_invoices.sql (total = taxable + tax) */
const INVOICES = [
  { num: "INV-2026-0301", cust: "Reliance Industries Ltd", total: 5900000, status: "paid", irn: true, hsn: "998314" },
  { num: "INV-2026-0302", cust: "Tata Consultancy Services", total: 3776000, status: "paid", irn: true, hsn: "998313" },
  { num: "INV-2026-0303", cust: "Infosys Ltd", total: 3304000, status: "delivered", irn: true, hsn: "998312" },
  { num: "INV-2026-0304", cust: "Mahindra & Mahindra", total: 1770000, status: "dispatched", irn: true, hsn: "84799090" },
  { num: "INV-2026-0305", cust: "Wipro Ltd", total: 1156400, status: "overdue", irn: true, hsn: "997331" },
  { num: "INV-2026-0306", cust: "L&T Finance Holdings", total: 4838000, status: "overdue", irn: true, hsn: "998311" },
  { num: "INV-2026-0307", cust: "Bajaj Finance Ltd", total: 1770000, status: "irn_pending", irn: false, hsn: "997314" },
  { num: "INV-2026-0308", cust: "HDFC Bank Ltd", total: 3245000, status: "disputed", irn: true, hsn: "998315" },
];

const FUNNEL = [
  { label: "Dispatched", n: 6, Icon: Send, c: C.primary },
  { label: "Delivered", n: 5, Icon: Mail, c: C.primary },
  { label: "Viewed", n: 3, Icon: Eye, c: C.warning },
  { label: "Clicked pay", n: 1, Icon: MousePointerClick, c: C.success },
];

const CH_ICON = { email: Mail, whatsapp: MessageSquare, sms: Smartphone };

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall },
  { code: "M2", name: "Dispute Resolution", icon: Scale },
  { code: "M3", name: "Contract MIS", icon: FileText },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp },
  { code: "M5", name: "Invoicing Engine", icon: Receipt, active: true },
  { code: "M6", name: "Payment Reminders", icon: Send },
  { code: "M7", name: "Cash Application", icon: Banknote },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid },
];

function Badge({ children, color }) {
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color, background: `${color}14`, border: `0.5px solid ${color}33`, borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap" }}>{children}</span>;
}
function KPICard({ label, value, sub, accent }) {
  return <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 150 }}>
    <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
  </div>;
}

export default function Invoicing() {
  const [view, setView] = useState("data");
  const [drawer, setDrawer] = useState(null);
  const [query, setQuery] = useState("");

  const rows = useMemo(() => INVOICES.filter(i => i.cust.toLowerCase().includes(query.toLowerCase())), [query]);
  const kpis = useMemo(() => ({
    invoiced: INVOICES.reduce((s, i) => s + i.total, 0),
    paid: INVOICES.filter(i => i.status === "paid").length,
    overdue: INVOICES.filter(i => i.status === "overdue").length,
    pendingIrn: INVOICES.filter(i => !i.irn).length,
  }), []);

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .rrow:hover{background:${C.bg}!important}.nav:hover{background:#ffffff14}`}</style>

      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {MODULES.map(m => { const Icon = m.icon; return (
          <div key={m.code} className="nav" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: m.active ? C.primary : "transparent", color: m.active ? "#fff" : "#AEB4C0" }}>
            <Icon size={15} /><span style={{ fontSize: 12.5 }}>{m.name}</span>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
          </div>); })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M5 · INVOICE GENERATION + MULTI-CHANNEL ENGAGEMENT</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Invoicing & Delivery</h1>
          </div>
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer", background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted }}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Total Invoiced" value={formatINR(kpis.invoiced)} sub="across 8 invoices" />
          <KPICard label="Paid" value={kpis.paid} sub="settled" accent={C.success} />
          <KPICard label="Overdue" value={kpis.overdue} sub="past due date" accent={C.danger} />
          <KPICard label="Awaiting IRN" value={kpis.pendingIrn} sub="blocked from dispatch" accent={C.warning} />
        </div>

        {/* GST gate banner */}
        {kpis.pendingIrn > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#FBF2E0", border: `0.5px solid ${C.warning}44`, borderRadius: 10, padding: "11px 14px", marginTop: 16 }}>
            <FileWarning size={16} color={C.warning} />
            <span style={{ fontSize: 13 }}><b>{kpis.pendingIrn} invoice</b> is awaiting a GST IRN and cannot be dispatched until one is generated. Bharat Industries’ turnover (₹800Cr) makes IRN mandatory.</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 540px", minWidth: 320 }}>
            {/* EF-CORE re-send panel */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · ENGAGEMENT</span>
                <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 9, color: C.ai }}>confidence 0.76</span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "#2A2A2E" }}>
                <b>INV-2026-0305 (Wipro)</b> is 50 days overdue — viewed via email but unpaid, and an SMS 12 days ago went unanswered.
                Recommended: re-send with a payment link via <b>WhatsApp</b>, and hand off to Collections (M1) if unpaid in 3 days.
              </p>
              <button onClick={() => setDrawer(INVOICES[4])} style={{ marginTop: 12, background: C.ai, color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Review & re-send</button>
            </div>

            {/* Invoice table */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: `0.5px solid ${C.border}` }}>
                <Search size={14} color={C.muted} />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter invoices…" style={{ border: "none", outline: "none", fontSize: 13, flex: 1, background: "transparent", color: C.text }} />
                <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{rows.length} invoices</span>
              </div>
              {view === "loading" && <Skeleton />}
              {view === "error" && <ErrorState />}
              {view === "empty" && <EmptyState />}
              {view === "data" && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Invoice / Customer", "Total", "IRN", "Status", ""].map((h, i) => (
                    <th key={h} style={{ textAlign: i === 1 ? "right" : "left", fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, fontWeight: 500, padding: "10px 14px", borderBottom: `0.5px solid ${C.border}` }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {rows.map((inv, i) => (
                      <tr key={i} className="rrow" onClick={() => setDrawer(inv)} style={{ cursor: "pointer", borderBottom: `0.5px solid ${C.border}` }}>
                        <td style={{ padding: "11px 14px" }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{inv.cust}</div>
                          <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, marginTop: 3 }}>{inv.num} · HSN {inv.hsn}</div>
                        </td>
                        <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 13 }}>{formatINR(inv.total)}</td>
                        <td style={{ padding: "11px 14px" }}>{inv.irn ? <CheckCircle2 size={15} color={C.success} /> : <Badge color={C.warning}>pending</Badge>}</td>
                        <td style={{ padding: "11px 14px" }}><Badge color={STATUS_COLOR[inv.status]}>{inv.status.replace("_", " ")}</Badge></td>
                        <td style={{ padding: "11px 14px", textAlign: "right", fontFamily: mono, fontSize: 10, color: C.muted }}>›</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Engagement funnel + governance */}
          <div style={{ flex: "0 0 250px", minWidth: 210 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, marginBottom: 4 }}>Engagement funnel</div>
              {FUNNEL.map((f, i) => {
                const Ic = f.Icon, pct = (f.n / FUNNEL[0].n) * 100;
                return (
                  <div key={i} style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}><Ic size={13} color={f.c} />{f.label}</span>
                      <span style={{ fontFamily: mono, fontSize: 12 }}>{f.n}</span>
                    </div>
                    <div style={{ height: 5, background: C.border, borderRadius: 3, marginTop: 5, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: f.c }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>GST compliance</div>
              {[["HSN on every line", "≥4 digits enforced"], ["IRN before dispatch", "blocked if missing"], ["total = taxable + tax", "computed at DB layer"]].map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12 }}>
                  <ShieldCheck size={15} color={C.success} style={{ marginTop: 1, flexShrink: 0 }} />
                  <div><div style={{ fontSize: 12.5, fontWeight: 500 }}>{r[0]}</div><div style={{ fontSize: 11, color: C.muted }}>{r[1]}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {drawer && <DispatchDrawer invoice={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function Skeleton() { return <div style={{ padding: 14 }}>{[...Array(5)].map((_, i) => <div key={i} style={{ height: 18, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />)}<style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style></div>; }
function ErrorState() { return <div style={{ padding: 40, textAlign: "center" }}><AlertTriangle size={26} color={C.danger} /><div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load invoices</div><button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}><RefreshCw size={13} /> Retry</button></div>; }
function EmptyState() { return <div style={{ padding: 44, textAlign: "center" }}><Inbox size={26} color={C.muted} /><div style={{ marginTop: 10, fontWeight: 500 }}>No invoices yet</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Generate an invoice to begin.</div></div>; }

function DispatchDrawer({ invoice, onClose }) {
  const [channel, setChannel] = useState("whatsapp");
  const [done, setDone] = useState(false);
  const hsnOk = /^\d{4,8}$/.test(invoice.hsn);
  const irnOk = invoice.irn;
  const dispatchable = !["paid", "cancelled"].includes(invoice.status);
  const blockReason = !dispatchable ? "Invoice is not in a dispatchable state."
    : !hsnOk ? "A line item is missing a valid HSN code."
    : !irnOk ? "GST IRN is required but missing — generate it first."
    : null;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 400, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Dispatch invoice</div>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <h3 style={{ margin: "8px 0 2px", fontSize: 17 }}>{invoice.cust}</h3>
        <div style={{ fontFamily: mono, fontSize: 13, color: C.muted }}>{invoice.num} · {formatINR(invoice.total)}</div>

        <div style={{ marginTop: 16, fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Channel</div>
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {["whatsapp", "email", "sms"].map(c => (
            <button key={c} onClick={() => setChannel(c)} style={{ flex: 1, fontSize: 12, padding: "8px 0", borderRadius: 8, cursor: "pointer", border: `0.5px solid ${channel === c ? C.primary : C.border}`, background: channel === c ? `${C.primary}10` : C.surface, color: channel === c ? C.primary : C.text, textTransform: "capitalize" }}>{c}</button>
          ))}
        </div>

        {/* GST gate checks */}
        <div style={{ marginTop: 16, background: blockReason ? "#FBE9E7" : "#E9F7F1", border: `0.5px solid ${(blockReason ? C.danger : C.success)}33`, borderRadius: 8, padding: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {blockReason ? <AlertTriangle size={14} color={C.danger} /> : <ShieldCheck size={14} color={C.success} />}
            <span style={{ fontSize: 12.5, fontWeight: 500, color: blockReason ? C.danger : C.success }}>{blockReason ? "Blocked by GST gate" : "Passes GST gate"}</span>
          </div>
          <ul style={{ margin: "8px 0 0 18px", padding: 0, fontSize: 11.5, color: C.muted }}>
            <li>HSN code valid — {hsnOk ? `yes (${invoice.hsn})` : "missing/invalid"}</li>
            <li>GST IRN present — {irnOk ? "yes" : "no (generate first)"}</li>
            <li>Dispatchable status — {dispatchable ? "yes" : `no (${invoice.status})`}</li>
          </ul>
        </div>

        {done ? (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, background: "#E9F7F1", border: `0.5px solid ${C.success}33`, borderRadius: 9, padding: 12, fontSize: 13, color: C.success }}>
            <CheckCircle2 size={15} /> Dispatched via {channel}. Logged to the audit trail.
          </div>
        ) : (
          <button disabled={!!blockReason} onClick={() => setDone(true)} style={{
            width: "100%", marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: blockReason ? C.border : C.primary, color: blockReason ? C.muted : "#fff",
            border: "none", borderRadius: 9, padding: "11px 0", fontSize: 13, fontWeight: 500,
            cursor: blockReason ? "not-allowed" : "pointer",
          }}>
            <Send size={14} /> {blockReason ? "Cannot dispatch" : `Dispatch via ${channel}`}
          </button>
        )}
        {blockReason && !irnOk && !done && (
          <button onClick={() => { invoice.irn = true; setDone(false); }} style={{ width: "100%", marginTop: 8, background: "transparent", color: C.primary, border: `0.5px solid ${C.primary}55`, borderRadius: 9, padding: "9px 0", fontSize: 12.5, cursor: "pointer" }}>
            Generate IRN first
          </button>
        )}
      </div>
    </div>
  );
}
